import { Button, Col, Drawer, message, Row, Spin } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import RegistrationTemplateForm, {
  RegistrationTemplateFormValues,
} from '../../../components/RegistrationTemplateForm/RegistrationTemplateForm';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';

const EditRegistrationTemplateDrawer = () => {
  const config = useAPIQuery('/configs/tenant-configurations/');
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { tenant, registration_template_id } = useTypedParams({
    tenant: String,
    registration_template_id: Number,
  });

  const navigate = useTypedNavigate();

  const [searchParams] = useSearchParams();

  const [setIsDirty] = useDiscardChangesModal(form);

  const handleClose = () =>
    navigate({
      pathname: '/:tenant/settings/registration-templates/',
      params: { tenant },
      search: searchParams,
    });

  const registrationTemplate = useAPIQuery(`/registration-templates/${registration_template_id}/`);

  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (
      {
        defaultTemplate,
        linkedinOrResume,
        pcdReport,
        ...variables
      }: RegistrationTemplateFormValues,
      api
    ) => {
      if (defaultTemplate) {
        await api.patch('/configs/tenant-configurations/default-registration-template-id/', {
          value: registration_template_id,
        });
      }

      return api.patch(`/registration-templates/${registration_template_id}/`, {
        ...variables,
        linkedin_or_resume: linkedinOrResume,
        pcd_report: pcdReport,
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Formulário de inscrição atualizado!'));
        handleClose();
      },
      onSettled: () => {
        queryClient.invalidateQueries('/registration-templates/');
        queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <Drawer
      title={registrationTemplate.data?.name}
      width={550}
      destroyOnClose
      open
      onClose={handleClose}
      footer={
        <Row gutter={8}>
          <Col>
            <Button type="primary" loading={mutation.isLoading} onClick={() => form.submit()}>
              <Trans>Salvar</Trans>
            </Button>
          </Col>
          <Col>
            <Button onClick={handleClose}>
              <Trans>Cancelar</Trans>
            </Button>
          </Col>
        </Row>
      }
    >
      <ErrorResult error={registrationTemplate.error || config.error}>
        <Spin spinning={registrationTemplate.isLoading || config.isLoading}>
          <Form<RegistrationTemplateFormValues>
            layout="vertical"
            form={form}
            initialValues={{
              name: registrationTemplate.data?.name,
              linkedinOrResume: registrationTemplate.data?.linkedin_or_resume,
              linkedin: registrationTemplate.data?.linkedin,
              resume: registrationTemplate.data?.resume,
              cpf: registrationTemplate.data?.cpf,
              pcd: registrationTemplate.data?.pcd,
              cid: registrationTemplate.data?.cid,
              pcdReport: registrationTemplate.data?.pcd_report,
              birthday: registrationTemplate.data?.birthday,
              salary: registrationTemplate.data?.salary,
              address: registrationTemplate.data?.address,
              relocation: registrationTemplate.data?.relocation,
              travel: registrationTemplate.data?.travel,
              portfolio: registrationTemplate.data?.portfolio,
              defaultTemplate:
                config.data?.defaultRegistrationTemplateId === registrationTemplate.data?.id,
            }}
            onFinish={(variables) => {
              mutation.mutate(variables);
            }}
            onValuesChange={() => setIsDirty(true)}
          >
            <RegistrationTemplateForm
              isSankhyaActive={config.data?.enableSankhyaAdmission}
              isVixtingActive={config.data?.enableVixtingAdmission}
              disableDefaultTemplateCheckbox={
                config.data?.defaultRegistrationTemplateId === registrationTemplate.data?.id
              }
              onAddField={() => setIsDirty(true)}
              onRemoveField={() => setIsDirty(true)}
            />
          </Form>
        </Spin>
      </ErrorResult>
    </Drawer>
  );
};

export default EditRegistrationTemplateDrawer;
